import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, AlertTriangle, CheckCircle2, Clock, X } from "lucide-react";

const STATUS_COLORS = {
  open: "bg-red-50 text-red-700 border-red-100",
  in_progress: "bg-amber-50 text-amber-700 border-amber-100",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-100",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function Grievances() {
  const { t } = useLang();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => {
    const params = {};
    if (user?.panchayat_id) params.panchayat_id = user.panchayat_id;
    if (filter) params.status = filter;
    api.get("/grievances", { params }).then((r) => setList(r.data));
  };
  useEffect(() => { load(); }, [filter, user]);

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">{t("grievances")}</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900">{t("grievances")}</h1>
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 rounded-md border border-slate-300 bg-white text-sm" data-testid="griev-filter">
            <option value="">All</option>
            {["open", "in_progress", "resolved", "closed"].map((s) => <option key={s} value={s}>{t(s)}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 rounded-md border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm" data-testid="griev-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-4 py-3">{t("ticketNo")}</th>
                  <th className="text-left px-4 py-3">{t("subject")}</th>
                  <th className="text-left px-4 py-3">{t("category")}</th>
                  <th className="text-left px-4 py-3">{t("status")}</th>
                  <th className="text-left px-4 py-3">SLA</th>
                </tr>
              </thead>
              <tbody>
                {list.map((g) => (
                  <tr key={g.id} className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${selected?.id === g.id ? "bg-[#0A3161]/5" : ""}`}
                      onClick={() => setSelected(g)} data-testid={`griev-row-${g.ticket_no}`}>
                    <td className="px-4 py-3 font-mono text-xs">{g.ticket_no}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{g.subject}</div>
                      <div className="text-xs text-slate-500">{g.citizen_name} · {g.mobile}</div>
                    </td>
                    <td className="px-4 py-3 capitalize">{g.category}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded font-semibold border ${STATUS_COLORS[g.status]}`}>{t(g.status)}</span>
                    </td>
                    <td className="px-4 py-3">{g.sla_days} {t("days")}</td>
                  </tr>
                ))}
                {list.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-slate-400 text-sm">No grievances</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="lg:col-span-2">
            {selected ? (
              <GrievanceDetail g={selected} onUpdate={load} />
            ) : (
              <div className="rounded-md border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
                Select a grievance to view details
              </div>
            )}
          </div>
        </div>
      </div>
    </OfficerLayout>
  );
}

function GrievanceDetail({ g, onUpdate }) {
  const { t } = useLang();
  const [status, setStatus] = useState(g.status);
  const [notes, setNotes] = useState(g.resolution_notes || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setStatus(g.status); setNotes(g.resolution_notes || ""); }, [g.id]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/grievances/${g.id}`, { status, resolution_notes: notes });
      toast.success("Updated");
      onUpdate();
    } catch (e) {
      toast.error("Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="griev-detail">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#E87722] font-semibold">{t("ticketNo")}</div>
      <div className="font-mono font-bold text-lg text-slate-900 mt-1">{g.ticket_no}</div>

      <div className="mt-4 space-y-3 text-sm">
        <Field label={t("subject")} value={g.subject} />
        <Field label={t("description")} value={g.description} />
        <Field label={t("category")} value={g.category} />
        <Field label="Citizen" value={`${g.citizen_name} · ${g.mobile}`} />
        {g.location && <Field label={t("location")} value={g.location} />}
        <Field label="SLA" value={`${g.sla_days} ${t("days")}`} />
        <Field label="Filed" value={new Date(g.created_at).toLocaleString("en-IN")} />
      </div>

      <div className="mt-5 pt-5 border-t border-slate-100 space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{t("status")}</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="griev-status-select"
            className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm">
            {["open", "in_progress", "resolved", "closed"].map((s) => <option key={s} value={s}>{t(s)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Resolution notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="griev-notes"
            className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm outline-none focus:border-[#0A3161]" />
        </div>
        <button onClick={save} disabled={busy} data-testid="griev-save"
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
          {busy ? "…" : t("save")}
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className="text-slate-900 mt-0.5">{value}</div>
  </div>
);
